import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useSaves } from '../hooks/useSaves';
import { useCustomTabs } from '../hooks/useCustomTabs';
import { useAuth } from '../contexts/AuthContext';
import styled from 'styled-components';
import SaveMenu from './saves/SaveMenu';
import AddSaveModal from './AddSaveModal';
import EditTagsModal from './saves/EditTagsModal'; // Import EditTagsModal
import SaveFilterModal from './saves/SaveFilterModal'; // Import SaveFilterModal
import SavePreviewModal from './saves/SavePreviewModal'; // Import SavePreviewModal
import TabSettingsModal from './saves/TabSettingsModal';
import ImportExportSavesModal from './saves/ImportExportSavesModal';
import ConfirmationModal from './ConfirmationModal';
import { FilterIcon } from './icons/FilterIcon'; // Import FilterIcon
import { SearchIcon } from './icons/SearchIcon'; // Import SearchIcon
import { ViewIcon } from './icons/ViewIcon';
import CloseIcon from './icons/CloseIcon';
import { SettingsIcon } from './icons/SettingsIcon';
import BackupIcon from './icons/BackupIcon';
import SortIcon from './icons/SortIcon';
import SortAlphaAscIcon from './icons/SortAlphaAscIcon';
import SortAlphaDescIcon from './icons/SortAlphaDescIcon';
import SortRecentIcon from './icons/SortRecentIcon';
import SortOldestIcon from './icons/SortOldestIcon';
import SortDropdownMenu from './saves/SortDropdownMenu';
import PlusIcon from './icons/PlusIcon';
import { format } from 'date-fns';
import { SAVE_SORT_OPTIONS, SAVE_READ_STATUS_FILTERS } from '../constants/saveConstants';
import { exportSaves } from '../utils/exportUtils';

const SavesSectionContainer = styled.div`
    padding: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 1;
    width: 100%; /* Ensure container takes full width */

    @media (max-width: 768px) {
        padding: 16px 4px;
    }
`;

const ResizableContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    background: rgba(1, 1, 1, 0.5);
    backdrop-filter: blur(10px);
    border-radius: 8px;
    padding: ${({ isCompactView }) => isCompactView ? '8px' : '16px'};
    min-width: 300px; /* Minimum width for resizing */
    max-width: 100%; /* Allow it to expand up to 100% of parent */
    box-sizing: border-box;

    @media (max-width: 768px) {
        width: 100% !important;
        min-width: 0;
    }
`;

const ResizerHandle = styled.div`
    width: 8px;
    cursor: ew-resize;
    position: absolute;
    top: 0;
    bottom: 0;
    right: -4px; /* Half of its width to be on the outside */
    z-index: 1001;
    background: transparent; /* Make it invisible by default */
    &:hover {
        background: rgba(255, 255, 255, 0.1); /* Show handle on hover */
    }

    @media (max-width: 768px) {
        display: none;
    }
`;

const SavesGrid = styled.div`
    width: 100%; /* Will be controlled by state */
    position: relative;
`;

const SavesList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: ${({ isCompactView }) => isCompactView ? 'flex' : 'grid'};
    grid-template-columns: ${({ isCompactView }) => isCompactView ? 'none' : 'repeat(auto-fill, minmax(250px, 1fr))'};
    gap: 12px;
    overflow-x: ${({ isCompactView }) => isCompactView ? 'auto' : 'hidden'};
    flex-wrap: ${({ isCompactView }) => isCompactView ? 'nowrap' : 'wrap'};
    padding-bottom: ${({ isCompactView }) => isCompactView ? '8px' : '0'};

    /* Custom Scrollbar Styles */
    &::-webkit-scrollbar {
        height: 8px; /* Taller scrollbar for horizontal view */
    }

    &::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 4px;

        &:hover {
            background: rgba(255, 255, 255, 0.3);
        }
    }

    /* Firefox */
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) rgba(255, 255, 255, 0.05);
`;

const SaveItem = styled.li`
    background: rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    transition: transform 0.2s ease-in-out, background-color 0.2s ease-in-out;
    min-width: ${({ isCompactView }) => isCompactView ? '250px' : 'auto'};
    position: relative;
    z-index: ${props => props.isMenuOpen ? 101 : 1}; /* Conditional z-index */

    /* Cards act as links, so their text should not be selectable/highlightable */
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;

    &:hover {
        transform: translateY(-2px);
        background: rgba(255, 255, 255, 0.15);
    }

    &.read {
        opacity: 0.7;
    }
`;

const SaveLink = styled.a`
    display: block;
    padding: 12px 16px;
    padding-right: 48px;
    text-decoration: none;
    color: #B6C2CF;
    font-weight: 500;
    font-size: 14px;
    transition: color 0.2s ease-in-out;
    position: relative;
    word-break: break-word;
    display: flex;
    flex-direction: column;
    gap: 4px;


    &:hover {
        color: #fff;
    }
`;

const SaveTitle = styled.span`
    font-weight: 500;
    color: #B6C2CF;
    font-size: 14px;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const SaveDate = styled.span`
    font-size: 10px;
    color: #888;
    opacity: 0.8;
`;

const TagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 8px; /* Add some space above the tags */
`;

const TagBadge = styled.span`
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
    color: #fff; /* Default text color, can be adjusted based on contrast */
    background-color: ${props => props.color || '#777'};
    display: inline-block;
`;

const EditableTitleInput = styled.input`
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    color: #fff;
    padding: 4px 8px;
    font-size: 14px;
    width: calc(100% - 16px);
    box-sizing: border-box;

    /* Editing needs normal selection inside the input despite the card's user-select: none */
    -webkit-user-select: text;
    -moz-user-select: text;
    -ms-user-select: text;
    user-select: text;

    &:focus {
        outline: none;
        border-color: #007bff;
    }
`;
 
 const UnreadIndicator = styled.div`
     position: absolute;
     top: 50%;
     right: 32px;
     transform: translateY(-50%);
     width: 8px;
     height: 8px;
     border-radius: 50%;
     background-color: #2ecc71;
 `;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    gap: 16px;
    flex-wrap: wrap;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    overflow-x: ${({ isSortMenuOpen }) => isSortMenuOpen ? 'visible' : 'auto'};
    padding-bottom: 8px;

    /* Custom Scrollbar Styles */
    &::-webkit-scrollbar {
        height: 8px;
    }

    &::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 4px;

        &:hover {
            background: rgba(255, 255, 255, 0.3);
        }
    }

    /* Firefox */
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) rgba(255, 255, 255, 0.05);
`;

const SearchInput = styled.input`
    background: transparent; /* Input is now transparent */
    border: none; /* No border for the input itself */
    outline: none; /* No outline on focus */
    color: #fff; /* Maintain text color */
    font-size: 14px;
    width: 100%; /* Fill the SearchContainer */
    padding: 4px 0; /* Updated vertical padding, horizontal is 0 */
    box-sizing: border-box;
    flex-grow: 1; /* Ensure it expands to fill available space */
    /* Remove focus border-color, as border is none. Focus indication might be on SearchContainer or none. */
`;

// SearchContainer now defines the input field's appearance
const SearchContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    flex-grow: 1;
    max-width: 250px;
    min-width: 50px;
    background: rgba(255, 255, 255, 0.2); /* Updated background */
    /* border removed */
    border-radius: 3px; /* Updated border-radius */
    padding: 4px 8px; /* Updated padding */
    /* &:focus-within { border-color: #007bff; } Optional: focus indication on container */
    /* display: flex and align-items: center are already part of this styled component */

    svg {
        color: #fff; /* Icon color */
        flex-shrink: 0; /* Prevent icons from shrinking */
        /* Default size will be used, or can be set via props on the icon components if needed */
    }
`;

const TabsContainer = styled.div`
    display: flex;
    flex-wrap: nowrap;
    gap: 12px;
    position: relative;
    z-index: 101;
    overflow-x: auto;
    padding-bottom: 8px; /* Space for scrollbar */
    flex-shrink: 1;
    min-width: 0;

    /* Custom Scrollbar Styles */
    &::-webkit-scrollbar {
        height: 8px;
    }

    &::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 4px;

        &:hover {
            background: rgba(255, 255, 255, 0.3);
        }
    }

    /* Firefox */
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) rgba(255, 255, 255, 0.05);
`;

const Tab = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    color: ${props => props.active ? '#fff' : '#B6C2CF'};
    font-size: 14px;
    font-weight: 500;
    padding: 8px 0;
    cursor: pointer;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: ${props => props.active ? '#fff' : 'transparent'};
        transition: background-color 0.2s ease;
    }

    &:hover {
        color: #fff;
    }

    span {
        opacity: 0.7;
        font-size: 12px;
    }

    div {
        display: none;
        margin-left: 4px;
    }

    &:hover div {
        display: flex;
        align-items: center;
    }

    .settings-icon {
        display: none;
    }

    &:hover .settings-icon {
        display: block;
    }

    svg {
        width: 12px;
        height: 12px;
        stroke: #B6C2CF;
    }
`;

const AddButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    color: #B6C2CF;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: auto;
    flex-shrink: 0;

    &:hover {
        background: rgba(255, 255, 255, 0.2);
        color: #fff;
    }

    svg {
        width: 16px;
        height: 16px;
    }
`;

const NoSavesMessage = styled.div`
    text-align: center;
    color: #B6C2CF;
    padding: 32px;
    font-size: 14px;
`;

const LoadingState = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    position: relative;
    z-index: 100;
`;

const LoadingSpinner = styled.div`
    width: 32px;
    height: 32px;
    border: 3px solid #f5f6fa;
    border-top-color: #0984e3;
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`;

const AddTabInput = styled.input`
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    color: #fff;
    padding: 4px 8px;
    font-size: 14px;
    width: 120px;
    box-sizing: border-box;
    &:focus {
        outline: none;
        border-color: #007bff;
    }
`;

const SavesSection = ({ showToast }) => {
    const savesSectionContainerRef = useRef(null);
    const { user } = useAuth();
    const {
        saves,
        loading,
        error,
        viewMode,
        counts,
        tabs,
        addSave,
        removeSave,
        toggleArchive,
        markAsRead,
        moveSaveToTab,
        moveSaveToBookmarks,
        updateSaveTitle,
        updateSaveData,
        fetchSaveContent,
        contentLoading,
        setViewMode,
        setSortBy,
        setSearchTerm,
        allSaves,
        allUniqueTags, // Destructure allUniqueTags
        selectedTags, // Destructure selectedTags
        setSelectedTags, // Destructure setSelectedTags
        readStatus, // Read/Unread status filter
        setReadStatus // Destructure setReadStatus
    } = useSaves(user?.uid);
    const { addTab, deleteTab, updateTabConfig } = useCustomTabs(user?.uid);
    const [newTabName, setNewTabName] = useState('');
    const [isAddingTab, setIsAddingTab] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [selectedTab, setSelectedTab] = useState(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [tabToDelete, setTabToDelete] = useState(null);
    const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
    const [currentSort, setCurrentSort] = useState(SAVE_SORT_OPTIONS.RECENT);

    const [savesSectionWidth, setSavesSectionWidth] = useLocalStorage('savesSectionWidth', 800); // Initial width
    const [isCompactView, setIsCompactView] = useLocalStorage('savesCompactView', false);
    const [nonCompactWidth, setNonCompactWidth] = useLocalStorage('savesNonCompactWidth', 800);
    const [isResizing, setIsResizing] = useState(false);
    const initialMouseX = useRef(0);
    const initialWidth = useRef(0);

    const [editingSaveId, setEditingSaveId] = useState(null);
    const [editedTitle, setEditedTitle] = useState('');
    const inputRef = useRef(null);
    const [openMenuId, setOpenMenuId] = useState(null);

    const handleMouseDown = (e) => {
        // Stop the drag from selecting text (e.g. inside the save cards) while resizing
        e.preventDefault();
        setIsResizing(true);
        initialMouseX.current = e.clientX;
        initialWidth.current = savesSectionWidth;
    };

    const handleMouseMove = useCallback((e) => {
        if (!isResizing) return;
        const deltaX = e.clientX - initialMouseX.current;
        const newWidth = Math.max(300, initialWidth.current + deltaX); // Min width of 300px
        setSavesSectionWidth(newWidth);
    }, [isResizing]);

    const handleMouseUp = useCallback(() => {
        setIsResizing(false);
    }, []);

    const handleToggleCompactView = () => {
        if (isCompactView) {
            // from compact to grid
            setSavesSectionWidth(nonCompactWidth);
        } else {
            // from grid to compact
            setNonCompactWidth(savesSectionWidth);
            if (savesSectionContainerRef.current) {
                const fullWidth = savesSectionContainerRef.current.clientWidth;
                setSavesSectionWidth(fullWidth);
            }
        }
        setIsCompactView(!isCompactView);
    };

    useEffect(() => {
        if (isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);

    useEffect(() => {
        // On initial load, if in compact view, set to full width.
        if (isCompactView && savesSectionContainerRef.current) {
            const fullWidth = savesSectionContainerRef.current.clientWidth;
            if (savesSectionWidth !== fullWidth) {
               setSavesSectionWidth(fullWidth);
            }
        }
    }, []); // Run only once on mount.

    useEffect(() => {
        if (editingSaveId && inputRef.current) {
            inputRef.current.focus();
        }
    }, [editingSaveId]);

    const handleTitleChange = (e) => {
        setEditedTitle(e.target.value);
    };

    const handleTitleBlur = async (saveId) => {
        if (editedTitle.trim() !== '') {
            await updateSaveTitle(saveId, editedTitle.trim());
        }
        setEditingSaveId(null);
        setEditedTitle('');
    };

    const handleTitleKeyDown = (e, saveId) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleTitleBlur(saveId);
        } else if (e.key === 'Escape') {
            setEditingSaveId(null);
            setEditedTitle('');
        }
    };

    const handleAddTab = async (e) => {
        if (e.key === 'Enter' && newTabName.trim()) {
            await addTab(newTabName.trim());
            setNewTabName('');
            setIsAddingTab(false);
        } else if (e.key === 'Escape') {
            setIsAddingTab(false);
            setNewTabName('');
        }
    };

    const handleOpenSettingsModal = (e, tab) => {
        e.stopPropagation();
        setSelectedTab(tab);
        setIsSettingsModalOpen(true);
    };

    const confirmDeleteTab = async () => {
        if (tabToDelete) {
            // Move saves to the main list
            const savesInTab = allSaves.filter(save => save.tabId === tabToDelete.id);
            for (const save of savesInTab) {
                await moveSaveToTab(save.id, null);
            }

            await deleteTab(tabToDelete.id);
            setIsConfirmModalOpen(false);
            setTabToDelete(null);
            setViewMode('active'); // Switch to a default view
        }
    };

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditTagsModalOpen, setIsEditTagsModalOpen] = useState(false);
    const [currentSaveForEdit, setCurrentSaveForEdit] = useState(null);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [isImportExportModalOpen, setIsImportExportModalOpen] = useState(false);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [currentSaveForPreview, setCurrentSaveForPreview] = useState(null);

    const openEditTagsModal = (save) => {
        setCurrentSaveForEdit(save);
        setIsEditTagsModalOpen(true);
    };

    const closeEditTagsModal = () => {
        setIsEditTagsModalOpen(false);
        setCurrentSaveForEdit(null);
    };

    const handleSaveTags = async (saveId, newTags) => {
        if (!user || !user.uid) {
            console.error("User not available for updating tags.");
            // Optionally show an error to the user
            return;
        }
        try {
            await updateSaveData(saveId, { tags: newTags });
            // Optionally, show a success message
            // The local state `saves` in useSaves should update via onSnapshot,
            // so no manual update of `currentSaveForEdit` tags here should be needed
            // unless immediate reflection before Firestore sync is desired.
        } catch (error) {
            console.error("Error updating tags in SavesSection:", error);
            // Optionally show an error to the user
        }
        closeEditTagsModal();
    };

    const handleToggleTagFilter = (tagToToggle) => {
        setSelectedTags(prevSelectedTags => {
            const isSelected = prevSelectedTags.some(
                tag => tag.name.toLowerCase() === tagToToggle.name.toLowerCase()
            );
            if (isSelected) {
                return prevSelectedTags.filter(
                    tag => tag.name.toLowerCase() !== tagToToggle.name.toLowerCase()
                );
            } else {
                return [...prevSelectedTags, tagToToggle];
            }
        });
    };

    const handleClearFilters = () => {
        setSelectedTags([]);
        setReadStatus(SAVE_READ_STATUS_FILTERS.ALL);
    };

    const handleSaveClick = async (e, save) => {
        e.preventDefault();
        setCurrentSaveForPreview(save);
        setIsPreviewModalOpen(true);
        // Fetch content if not already available
        if (!save.content) {
            const updatedSave = await fetchSaveContent(save.id);
            if (updatedSave) {
                setCurrentSaveForPreview(updatedSave);
            }
        }
        if (!save.read) {
            await markAsRead(save.id);
        }
    };

    const handleRemoveSave = (saveId) => {
        const element = document.getElementById(`save-${saveId}`);
        if (element) {
            element.style.opacity = '0';
            element.style.transform = 'translateX(20px)';
            setTimeout(() => {
                removeSave(saveId);
            }, 200);
        }
    };

    const handleToggleArchive = (saveId, shouldArchive) => {
        const element = document.getElementById(`save-${saveId}`);
        if (element) {
            element.style.opacity = '0';
            element.style.transform = 'translateX(20px)';
            setTimeout(() => {
                toggleArchive(saveId, shouldArchive);
            }, 200);
        }
    };

    const handleOpenFullPage = (url) => {
        window.open(url, '_blank', 'noopener,noreferrer');
        setIsPreviewModalOpen(false);
    };

    const handleExportSaves = (selectedSaveIds, format) => {
        const savesToExport = allSaves.filter(save => selectedSaveIds.includes(save.id));
        const { content, mimeType, fileExtension } = exportSaves(savesToExport, format);
        const blob = new Blob([content], { type: mimeType });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `saves-backup.${fileExtension}`;
        link.click();
        URL.revokeObjectURL(link.href);
        setIsImportExportModalOpen(false);
    };

    const handleCopySaves = async (selectedSaveIds, format) => {
        const savesToExport = allSaves.filter(save => selectedSaveIds.includes(save.id));
        const { content } = exportSaves(savesToExport, format);
        try {
            await navigator.clipboard.writeText(content);
            // Optional: Show a success message to the user
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
            // Optional: Show an error message to the user
        }
    };


    if (loading) {
        return (
            <SavesSectionContainer>
                <LoadingState>
                    <LoadingSpinner />
                </LoadingState>
            </SavesSectionContainer>
        );
    }

    if (error) {
        return (
            <SavesSectionContainer>
                <SavesGrid>
                    <NoSavesMessage>Error loading saves: {error}</NoSavesMessage>
                </SavesGrid>
            </SavesSectionContainer>
        );
    }

    return (
        <SavesSectionContainer ref={savesSectionContainerRef}>
            <ResizableContainer style={{ width: savesSectionWidth }} isCompactView={isCompactView}>
                <SavesGrid>
                    <Header>
                        <TabsContainer>
                            <Tab
                                active={viewMode === 'unread'}
                                onClick={() => setViewMode('unread')}
                            >
                                Unread
                                <span>({counts.unread})</span>
                            </Tab>
                            <Tab
                                active={viewMode === 'read'}
                                onClick={() => setViewMode('read')}
                            >
                                Read
                                <span>({counts.read})</span>
                            </Tab>
                            <Tab
                                active={viewMode === 'active'}
                                onClick={() => setViewMode('active')}
                            >
                                All
                                <span>({counts.active})</span>
                            </Tab>
                            {tabs.map(tab => (
                                <Tab
                                    key={tab.id}
                                    active={viewMode === tab.id}
                                    onClick={() => setViewMode(tab.id)}
                                >
                                    {tab.name}
                                    <span>({counts[tab.id] || 0})</span>
                                    <div className="settings-icon" onClick={(e) => handleOpenSettingsModal(e, tab)}>
                                        <SettingsIcon />
                                    </div>
                                </Tab>
                            ))}
                            <Tab
                                active={viewMode === 'archived'}
                                onClick={() => setViewMode('archived')}
                            >
                                Archived
                                <span>({counts.archived})</span>
                            </Tab>
                            {isAddingTab ? (
                                <AddTabInput
                                    type="text"
                                    placeholder="New Tab"
                                    value={newTabName}
                                    onChange={(e) => setNewTabName(e.target.value)}
                                    onKeyDown={handleAddTab}
                                    onBlur={() => {
                                        setIsAddingTab(false);
                                        setNewTabName('');
                                    }}
                                    autoFocus
                                />
                            ) : (
                                <AddButton onClick={() => setIsAddingTab(true)} title="Add new tab">
                                    <PlusIcon />
                                </AddButton>
                            )}
                        </TabsContainer>
                        <HeaderActions isSortMenuOpen={isSortMenuOpen}>
                            <SearchContainer>
                                <SearchIcon style={{ marginRight: '8px' }} />
                                <SearchInput
                                    type="text"
                                    placeholder="Search saves..."
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <FilterIcon onClick={() => setIsFilterModalOpen(true)} style={{ marginRight: '8px' }}/>
                                {currentSort === SAVE_SORT_OPTIONS.RECENT && <SortRecentIcon onClick={() => setIsSortMenuOpen(true)} />}
                                {currentSort === SAVE_SORT_OPTIONS.OLDEST && <SortOldestIcon onClick={() => setIsSortMenuOpen(true)} />}
                                {currentSort === SAVE_SORT_OPTIONS.ALPHA_ASC && <SortAlphaAscIcon onClick={() => setIsSortMenuOpen(true)} />}
                                {currentSort === SAVE_SORT_OPTIONS.ALPHA_DESC && <SortAlphaDescIcon onClick={() => setIsSortMenuOpen(true)} />}
                                <SortDropdownMenu
                                    isOpen={isSortMenuOpen}
                                    onClose={() => setIsSortMenuOpen(false)}
                                    setSortBy={(sort) => {
                                        setSortBy(sort);
                                        setCurrentSort(sort);
                                    }}
                                    currentSort={currentSort}
                                />
                            </SearchContainer>
                            <AddButton onClick={handleToggleCompactView} title={isCompactView ? "Grid View" : "Compact View"}>
                                <ViewIcon />
                            </AddButton>
                            <AddButton onClick={() => setIsImportExportModalOpen(true)}>
                                <BackupIcon />
                            </AddButton>
                            <AddButton onClick={() => setIsAddModalOpen(true)}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 5v14M5 12h14" strokeLinecap="round"/>
                                </svg>
                            </AddButton>
                        </HeaderActions>
                    </Header>
                    {saves.length > 0 ? (
                        <SavesList isCompactView={isCompactView}>
                            {saves.map(save => (
                                <SaveItem
                                    key={save.id}
                                    id={`save-${save.id}`}
                                    className={save.read ? 'read' : ''}
                                    style={{ transition: 'all 0.2s ease-in-out' }}
                                    isMenuOpen={openMenuId === save.id} // Pass isMenuOpen prop
                                    isCompactView={isCompactView}
                                >
                                    <SaveLink
                                        href={save.url}
                                        onClick={(e) => handleSaveClick(e, save)}
                                        title={save.title}
                                    >
                                        {editingSaveId === save.id ? (
                                            <EditableTitleInput
                                                ref={inputRef}
                                                value={editedTitle}
                                                onChange={handleTitleChange}
                                                onBlur={() => handleTitleBlur(save.id)}
                                                onKeyDown={(e) => handleTitleKeyDown(e, save.id)}
                                            />
                                        ) : (
                                            <SaveTitle>
                                                {save.title}
                                            </SaveTitle>
                                        )}
                                        {save.createdAt && (
                                            <SaveDate>
                                                {(() => {
                                                    let date;
                                                    if (save.createdAt && typeof save.createdAt.toDate === 'function') {
                                                        date = save.createdAt.toDate();
                                                    } else if (save.createdAt && typeof save.createdAt === 'object' && save.createdAt.seconds) {
                                                        date = new Date(save.createdAt.seconds * 1000);
                                                    } else if (save.createdAt) {
                                                        date = new Date(save.createdAt);
                                                    }
                                                    return date ? format(date, 'MMM dd, yyyy HH:mm') : 'Invalid Date';
                                                })()}
                                            </SaveDate>
                                        )}
                                        {/* Tags Display */}
                                        {save.tags && save.tags.length > 0 && (
                                            <TagsContainer>
                                                {save.tags.map((tag, index) => (
                                                    <TagBadge key={index} color={tag.color}>
                                                        {tag.name}
                                                    </TagBadge>
                                                ))}
                                            </TagsContainer>
                                        )}
                                        {!save.read && <UnreadIndicator role="img" aria-label="Unread" />}
                                    </SaveLink>
                                    <SaveMenu
                                        saveId={save.id}
                                        isOpen={openMenuId === save.id}
                                        onToggle={() => setOpenMenuId(prevId => prevId === save.id ? null : save.id)}
                                        onDelete={() => handleRemoveSave(save.id)}
                                        onToggleArchive={() => handleToggleArchive(save.id, !save.archived)}
                                        isArchived={save.archived}
                                        onEditTitle={() => {
                                            setEditingSaveId(save.id);
                                            setEditedTitle(save.title);
                                            // setOpenMenuId(null); // Delegated to SaveMenu's onToggle
                                        }}
                                        isRead={save.read}
                                        onMarkAsRead={() => markAsRead(save.id)}
                                        onMarkAsUnread={() => markAsRead(save.id, false)}
                                        saveContent={save.url}
                                        onEditTags={() => {
                                            openEditTagsModal(save);
                                            setOpenMenuId(null); // Close menu when opening tags modal
                                        }}
                                        onMoveToTab={(saveId, tabId) => moveSaveToTab(saveId, tabId)}
                                        onMoveToBookmarks={() => {
                                            moveSaveToBookmarks(save);
                                            showToast(`Moved "${save.title}" to Bookmarks`);
                                        }}
                                        customTabs={tabs}
                                    />
                                </SaveItem>
                            ))}
                    </SavesList>
                ) : (
                    <NoSavesMessage>
                        {(selectedTags.length > 0 || readStatus !== SAVE_READ_STATUS_FILTERS.ALL)
                            ? 'No saves match the current filters.'
                            : viewMode === 'archived'
                                ? 'No archived saves yet.'
                                : viewMode === 'read'
                                    ? 'No read saves yet.'
                                    : viewMode === 'unread'
                                        ? 'No unread saves yet.'
                                        : 'No saves yet.'
                        }
                    </NoSavesMessage>
                )}
            </SavesGrid>
            <ResizerHandle data-testid="saves-resizer" onMouseDown={handleMouseDown} />
            </ResizableContainer>
            <AddSaveModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                userId={user?.uid}
                allUniqueTags={allUniqueTags} // Pass allUniqueTags
                allSaves={allSaves}
                addSave={addSave}
            />
            {currentSaveForEdit && (
                <EditTagsModal
                    isOpen={isEditTagsModalOpen}
                    onClose={closeEditTagsModal}
                    saveData={currentSaveForEdit}
                    onSave={handleSaveTags}
                    allUniqueTags={allUniqueTags} // Pass allUniqueTags
                allSaves={allSaves}
                />
            )}
            <SaveFilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                allUniqueTags={allUniqueTags}
                selectedTags={selectedTags}
                onToggleTagFilter={handleToggleTagFilter}
                readStatus={readStatus}
                onChangeReadStatus={setReadStatus}
                onClearFilters={handleClearFilters}
            />
            {currentSaveForPreview && (
                <SavePreviewModal
                    isOpen={isPreviewModalOpen}
                    onClose={() => setIsPreviewModalOpen(false)}
                    save={currentSaveForPreview}
                    onRefresh={fetchSaveContent}
                    onOpenFullPage={handleOpenFullPage}
                    contentLoading={contentLoading}
                />
            )}
            {isConfirmModalOpen && (
                <ConfirmationModal
                    onCancel={() => setIsConfirmModalOpen(false)}
                    onConfirm={confirmDeleteTab}
                    title="Delete Tab"
                    message={`Are you sure you want to delete the tab "${tabToDelete?.name}"? All saves within this tab will be moved to the main list.`}
                    confirmText="Delete"
                    cancelText="Cancel"
                />
            )}
            <ImportExportSavesModal
                isOpen={isImportExportModalOpen}
                onClose={() => setIsImportExportModalOpen(false)}
                allSaves={allSaves}
                filteredSaves={saves}
                tabs={tabs}
                onExport={handleExportSaves}
                onCopy={handleCopySaves}
                addSave={addSave}
            />
            <TabSettingsModal
                isOpen={isSettingsModalOpen}
                onClose={() => setIsSettingsModalOpen(false)}
                tab={selectedTab}
                allSaves={allSaves}
                moveSaveToTab={moveSaveToTab}
                deleteTab={deleteTab}
                setViewMode={setViewMode}
                allUniqueTags={allUniqueTags}
            />
        </SavesSectionContainer>
    );
};

export default React.memo(SavesSection);
